/**
 * รายละเอียดพื้น (รอบ 20 · ผู้ใช้ส่งคลิปต้นแบบ: พื้นมีลาย หญ้า/ดอกไม้หนาแน่น)
 *
 * 1. `groundMaterial` — texture พื้นวาดเองต่อธีม (DynamicTexture: จุด เส้น หย่อมสี) tiling ทั้งผืน
 * 2. `scatterGround` — กอหญ้า/ดอกไม้/ของเล็กหลายพันชิ้นด้วย **thin instances** (mesh ต้นแบบชิ้นเดียว = draw call เดียวต่อชนิด)
 *
 * ทุกอย่างเป็นภาพล้วน ไม่มีการชน ไม่รับคลิก · ตำแหน่งสุ่มจาก seed ที่ส่งเข้ามา (ทุกเครื่องเห็นเหมือนกัน)
 * วัสดุแคชต่อฉาก (WeakMap<Scene>) — วัสดุข้ามฉากจะไม่วาดอะไรเลย (กับดักใน AGENTS.md)
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3, Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math";
import "@babylonjs/core/Meshes/thinInstanceMesh";

export type GraphicsQuality = "high" | "low";

/** ลายพื้นต่อธีม: สีจุด/เส้นเพิ่มจากสีพื้น และชนิดลาย */
interface GroundStyle { marks: "grass" | "sand" | "snow" | "rock" | "lava" | "moss"; tint?: string }
const STYLE: Record<string, GroundStyle> = {
  desert: { marks: "sand" }, coast: { marks: "sand" },
  snow: { marks: "snow" }, dungeon_frost: { marks: "snow" }, celestial: { marks: "snow" },
  volcano: { marks: "lava" },
  ruins: { marks: "rock" }, root_cave: { marks: "rock" }, dungeon_mine: { marks: "rock" }, crystal_cave: { marks: "rock" },
  abyss_ruins: { marks: "rock" }, graveyard: { marks: "moss" }, swamp: { marks: "moss" }, storm: { marks: "rock" },
};

/** ปรับสีอ่อน/เข้ม (amt −1..1) */
function shade(c: Color3, amt: number): string {
  const f = (v: number): number => Math.round(255 * Math.max(0, Math.min(1, amt < 0 ? v * (1 + amt) : v + (1 - v) * amt)));
  return `rgb(${f(c.r)},${f(c.g)},${f(c.b)})`;
}

/** ตัวสุ่มจาก seed (mulberry32) */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const groundCache = new WeakMap<Scene, Map<string, StandardMaterial>>();

/** วัสดุพื้นมีลายของธีมนี้ · `span` = ความกว้างพื้น (หน่วยโลก) ใช้คิดจำนวนรอบ tiling */
export function groundMaterial(scene: Scene, theme: string, groundHex: string, span: number): StandardMaterial {
  let per = groundCache.get(scene);
  if (!per) { per = new Map(); groundCache.set(scene, per); }
  const key = `${theme}|${groundHex}|${Math.round(span)}`;
  const hit = per.get(key);
  if (hit) return hit;

  const style = STYLE[theme] ?? { marks: "grass" };
  const base = Color3.FromHexString(groundHex);
  const m = new StandardMaterial(`gmat-${key}`, scene);
  m.specularColor = Color3.Black();
  per.set(key, m);
  // ไม่มี canvas (เทสต์ NullEngine / เครื่องแปลก) → สีเรียบแบบเดิม เกมต้องเปิดได้เสมอ
  if (typeof document === "undefined" && typeof OffscreenCanvas === "undefined") {
    m.diffuseColor = base;
    return m;
  }
  const N = 512;
  const tex = new DynamicTexture(`gtex-${key}`, { width: N, height: N }, scene, true);
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  const r = rng(theme.length * 977 + N);
  ctx.fillStyle = shade(base, 0);
  ctx.fillRect(0, 0, N, N);
  // หย่อมสีใหญ่นุ่ม ๆ (วาดซ้ำขอบตรงข้ามให้ต่อกันไร้รอยต่อ)
  const blob = (x: number, y: number, rad: number, color: string, alpha: number): void => {
    for (const dx of [-N, 0, N]) for (const dy of [-N, 0, N]) {
      const g = ctx.createRadialGradient(x + dx, y + dy, 0, x + dx, y + dy, rad);
      g.addColorStop(0, color);
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.globalAlpha = alpha;
      ctx.fillStyle = g;
      ctx.fillRect(x + dx - rad, y + dy - rad, rad * 2, rad * 2);
    }
  };
  for (let i = 0; i < 18; i++) blob(r() * N, r() * N, 90 + r() * 110, shade(base, r() > 0.5 ? 0.07 : -0.04), 0.45);
  ctx.globalAlpha = 1;
  // ลายเล็กตามชนิดพื้น
  const marks = style.marks;
  const n = marks === "grass" || marks === "moss" ? 2600 : 1400;
  for (let i = 0; i < n; i++) {
    const x = r() * N, y = r() * N;
    const k = r();
    if (marks === "grass" || marks === "moss") {
      // ขีดใบหญ้าสั้น ๆ เอียงสุ่ม
      ctx.strokeStyle = shade(base, k > 0.5 ? 0.09 : -0.09);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (r() - 0.5) * 5, y - 4 - r() * 5);
      ctx.stroke();
    } else if (marks === "sand") {
      ctx.fillStyle = shade(base, k > 0.5 ? 0.18 : -0.16);
      ctx.fillRect(x, y, 1.6, 1.6);
    } else if (marks === "snow") {
      ctx.fillStyle = shade(base, k > 0.5 ? 0.3 : 0.12);
      ctx.beginPath();
      ctx.arc(x, y, 1 + r() * 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // หิน/ลาวา: ก้อนหินแบนเหลี่ยม + รอยแตก
      ctx.fillStyle = shade(base, k > 0.5 ? 0.14 : -0.18);
      ctx.beginPath();
      const s = 2 + r() * 6;
      ctx.moveTo(x, y);
      ctx.lineTo(x + s, y + r() * s * 0.5);
      ctx.lineTo(x + s * 0.6, y + s);
      ctx.lineTo(x - s * 0.3, y + s * 0.7);
      ctx.closePath();
      ctx.fill();
    }
  }
  if (marks === "lava" || marks === "rock") {
    ctx.strokeStyle = marks === "lava" ? "rgba(255,110,30,0.75)" : shade(base, -0.3);
    ctx.lineWidth = marks === "lava" ? 2 : 1.2;
    for (let i = 0; i < 40; i++) {
      let x = r() * N, y = r() * N;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let j = 0; j < 5; j++) { x += (r() - 0.5) * 40; y += (r() - 0.5) * 40; ctx.lineTo(x, y); }
      ctx.stroke();
    }
  }
  tex.update();
  tex.wrapU = Texture.WRAP_ADDRESSMODE;
  tex.wrapV = Texture.WRAP_ADDRESSMODE;
  // หนึ่งรอบลาย ≈ 14 หน่วยโลก
  tex.uScale = span / 14;
  tex.vScale = span / 14;
  tex.anisotropicFilteringLevel = 4;

  m.diffuseTexture = tex;
  m.emissiveColor = marks === "lava" ? new Color3(0.08, 0.02, 0) : Color3.Black();
  return m;
}

/* ---------- ของเล็กบนพื้น (thin instances) ---------- */

interface ScatterKind { make: (scene: Scene) => Mesh; colors: string[]; per100: number; scale: [number, number] }

/** กอหญ้า: ใบแบนสามใบเอียงออก */
function tuft(scene: Scene): Mesh {
  const parts: Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const b = MeshBuilder.CreateCylinder("blade", { diameterTop: 0, diameterBottom: 0.3, height: 0.6, tessellation: 3 }, scene);
    b.position.y = 0.26;
    b.rotation.z = (i - 1) * 0.45;
    b.rotation.y = i * 2.1;
    b.bakeCurrentTransformIntoVertices();
    parts.push(b);
  }
  return Mesh.MergeMeshes(parts, true)!;
}

/** ดอกไม้: ก้าน + หัวกลม */
function flower(scene: Scene): Mesh {
  const head = MeshBuilder.CreateSphere("fh", { diameter: 0.24, segments: 4 }, scene);
  head.position.y = 0.32;
  head.bakeCurrentTransformIntoVertices();
  return head;
}

/** ก้อนเล็ก (หิน หิมะ ผลึก ถ่าน) */
function pebble(scene: Scene): Mesh {
  const p = MeshBuilder.CreateIcoSphere("pb", { radius: 0.18, subdivisions: 1 }, scene);
  p.scaling.y = 0.55;
  p.position.y = 0.05;
  p.bakeCurrentTransformIntoVertices();
  return p;
}

function shard(scene: Scene): Mesh {
  const s = MeshBuilder.CreateCylinder("sd", { diameterTop: 0, diameterBottom: 0.2, height: 0.5, tessellation: 4 }, scene);
  s.position.y = 0.22;
  s.bakeCurrentTransformIntoVertices();
  return s;
}

function kindsFor(theme: string, groundHex: string, petals: string[]): ScatterKind[] {
  const g = Color3.FromHexString(groundHex);
  const hex = (c: Color3): string => c.toHexString();
  const lighter = hex(Color3.Lerp(g, new Color3(0.75, 0.95, 0.4), 0.4));
  const darker = hex(Color3.Lerp(g, new Color3(0.35, 0.6, 0.25), 0.3));
  const grass: ScatterKind = { make: tuft, colors: [lighter, darker, hex(Color3.Lerp(g, Color3.White(), 0.15))], per100: 1400, scale: [1.1, 2] };
  const flowers: ScatterKind = { make: flower, colors: petals, per100: 160, scale: [1, 1.5] };
  switch (STYLE[theme]?.marks ?? "grass") {
    case "sand": return [
      { make: tuft, colors: ["#c9b36a", "#a89450", "#d8c88a"], per100: 260, scale: [0.6, 1.1] },
      { make: pebble, colors: ["#b8a27a", "#9a8460", "#d4c4a0"], per100: 160, scale: [0.6, 1.5] },
    ];
    case "snow": return [
      { make: pebble, colors: ["#f4f8fc", "#dce8f2", "#c8d8e8"], per100: 260, scale: [0.8, 2] },
      { make: shard, colors: ["#bfe6ff", "#9fd2f5", "#e6f6ff"], per100: 60, scale: [0.6, 1.3] },
      { make: tuft, colors: ["#6f8a7a", "#86a090"], per100: 120, scale: [0.6, 1] },
    ];
    case "lava": return [
      { make: pebble, colors: ["#2a1d1a", "#3c2a24", "#1d1412"], per100: 300, scale: [0.7, 1.8] },
      { make: shard, colors: ["#ff6a2a", "#ff9a3a"], per100: 40, scale: [0.4, 0.9] },
    ];
    case "rock": return [
      { make: pebble, colors: [darker, lighter, hex(g.scale(0.6))], per100: 280, scale: [0.6, 1.8] },
      { make: tuft, colors: [hex(Color3.Lerp(g, new Color3(0.45, 0.55, 0.3), 0.5)), darker], per100: 220, scale: [0.5, 1] },
      ...(theme === "crystal_cave" ? [{ make: shard, colors: ["#4fd1c5", "#9f7aea", "#63b3ed"], per100: 70, scale: [0.6, 1.6] as [number, number] }] : []),
    ];
    case "moss": return [
      { ...grass, per100: 650 },
      { make: pebble, colors: [darker, hex(g.scale(0.6))], per100: 120, scale: [0.6, 1.4] },
      { ...flowers, per100: 50 },
    ];
    default: return [grass, flowers, { make: pebble, colors: ["#8a8f96", "#a3a8ae"], per100: 30, scale: [0.6, 1.3] }];
  }
}

/** วัสดุของชิ้นเล็ก (สีขาว · สีจริงมาจาก thin instance "color") แคชต่อฉาก */
const scatterMat = new WeakMap<Scene, StandardMaterial>();
function whiteMat(scene: Scene): StandardMaterial {
  let m = scatterMat.get(scene);
  if (!m) {
    m = new StandardMaterial("scatter-mat", scene);
    m.diffuseColor = Color3.White();
    m.specularColor = Color3.Black();
    m.backFaceCulling = false;
    m.freeze();
    scatterMat.set(scene, m);
  }
  return m;
}

/**
 * โปรยของเล็กทั่วแผนที่ (ภายใน ±half) หลบจุดที่ `avoid` บอก
 * คืน mesh ต้นแบบทั้งหมด (ผู้เรียกจด/ทิ้งตอนสลับโซนเหมือน mesh อื่น)
 */
export function scatterGround(
  scene: Scene, theme: string, groundHex: string, half: number, seed: number,
  avoid: (x: number, z: number) => boolean, petals: string[], quality: GraphicsQuality,
): Mesh[] {
  const r = rng(seed * 131 + 7);
  const area = (half * 2) * (half * 2) / 10000; // หน่วย 100×100
  const mul = quality === "high" ? 1 : 0.3;
  const out: Mesh[] = [];
  const q = new Quaternion();
  const tmp = new Matrix();
  const sv = new Vector3(), tv = new Vector3();
  for (const kind of kindsFor(theme, groundHex, petals)) {
    const count = Math.round(kind.per100 * area * mul);
    if (count <= 0) continue;
    const mesh = kind.make(scene);
    mesh.name = "scatter";
    mesh.material = whiteMat(scene);
    mesh.isPickable = false;
    const mats = new Float32Array(count * 16);
    const cols = new Float32Array(count * 4);
    const palette = kind.colors.map((c) => Color3.FromHexString(c));
    let n = 0;
    for (let i = 0; i < count; i++) {
      const x = (r() * 2 - 1) * half, z = (r() * 2 - 1) * half;
      if (avoid(x, z)) continue;
      const s = kind.scale[0] + r() * (kind.scale[1] - kind.scale[0]);
      Quaternion.RotationYawPitchRollToRef(r() * Math.PI * 2, 0, 0, q);
      sv.set(s, s, s);
      tv.set(x, 0, z);
      Matrix.ComposeToRef(sv, q, tv, tmp);
      tmp.copyToArray(mats, n * 16);
      const c = palette[Math.floor(r() * palette.length)]!;
      const v = 0.9 + r() * 0.2;
      cols.set([c.r * v, c.g * v, c.b * v, 1], n * 4);
      n++;
    }
    mesh.thinInstanceSetBuffer("matrix", mats.subarray(0, n * 16), 16, true);
    mesh.thinInstanceSetBuffer("color", cols.subarray(0, n * 4), 4, true);
    mesh.thinInstanceRefreshBoundingInfo(false);
    mesh.freezeWorldMatrix();
    out.push(mesh);
  }
  return out;
}
